import type {
    TutorSource,
    TutorSourceBlock,
    TutorSourceBlockKind
} from "./TutorEngine.js";

const BLOCK_SELECTOR = "h1,h2,h3,h4,p,li,tr,blockquote,dt,dd";
const MAX_BLOCKS = 7;
const MAX_BLOCK_LENGTH = 320;
const MAX_SOURCE_LENGTH = 1800;

function normalizeTutorText(value: string): string {
    return value
        .replace(/^[\s💡⚠️📖🦖]+/u, "")
        .replace(/\s+/gu, " ")
        .trim();
}

function classifyElement(element: Element): TutorSourceBlockKind {
    const tag = element.tagName.toLowerCase();

    if (/^h[1-4]$/u.test(tag)) return "heading";
    if (tag === "li") return "list-item";
    if (tag === "tr") return "table-row";
    if (tag === "blockquote" || tag === "dd" || tag === "dt") return "note";

    if (element.closest("aside")) return "note";
    if (element.closest("[data-tutor-example]")) return "example";

    return "paragraph";
}

function elementText(element: Element): string {
    if (element.tagName.toLowerCase() === "tr") {
        return normalizeTutorText(
            [...element.children]
                .map(child => normalizeTutorText(child.textContent ?? ""))
                .filter(Boolean)
                .join(" · ")
        );
    }

    return normalizeTutorText(element.textContent ?? "");
}

function extractTutorSource(
    surface: HTMLElement,
    label: string
): TutorSource {
    const clone = surface.cloneNode(true) as HTMLElement;

    clone.querySelectorAll([
        ".dino-surface-peek",
        ".dino-tutor-answer",
        "button",
        "input",
        "select",
        "textarea",
        "script",
        "style",
        "svg",
        "[aria-hidden='true']",
        "[hidden]",
        "[contenteditable='true']"
    ].join(",")).forEach(element => element.remove());

    const candidates = [...clone.querySelectorAll(BLOCK_SELECTOR)]
        .filter(element => !element.parentElement?.closest(BLOCK_SELECTOR));
    const blocks: TutorSourceBlock[] = [];
    let totalLength = 0;

    for (const element of candidates) {
        const text = elementText(element).slice(0, MAX_BLOCK_LENGTH);

        if (!text || totalLength + text.length > MAX_SOURCE_LENGTH) {
            continue;
        }

        blocks.push({
            kind: classifyElement(element),
            text
        });
        totalLength += text.length;

        if (blocks.length >= MAX_BLOCKS) break;
    }

    if (blocks.length === 0) {
        const fallback = normalizeTutorText(clone.textContent ?? "")
            .slice(0, MAX_SOURCE_LENGTH);

        if (fallback) {
            blocks.push({ kind: "paragraph", text: fallback });
        }
    }

    return {
        label: normalizeTutorText(label) || "Selected learning content",
        blocks
    };
}

export {
    extractTutorSource,
    normalizeTutorText
};
